import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

let tokenGetter = async () => null;

export function setPartnerAuthTokenGetter(fn) {
  tokenGetter = fn;
}

async function authHeaders() {
  const token = await tokenGetter();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(method, path, data, config = {}) {
  const headers = {
    ...(await authHeaders()),
    ...(config.headers || {}),
  };
  try {
    const res = await axios({
      method,
      url: `${API_URL}${path}`,
      data,
      ...config,
      headers,
    });
    return res.data;
  } catch (err) {
    const message = err.response?.data?.error || err.message || "Request failed";
    const error = new Error(message);
    error.status = err.response?.status;
    error.data = err.response?.data;
    throw error;
  }
}

export const partnerApi = {
  sync: (body) => request("post", "/api/v1/auth/sync", body || {}),
  getPartner: () => request("get", "/api/v1/partner/me"),
  patchOrg: (body) => request("patch", "/api/v1/partner/me", body),
  putSources: (body) => request("put", "/api/v1/partner/me/sources", body),
  acceptAgreement: (body) => request("post", "/api/v1/partner/me/accept-agreement", body || {}),
  submit: () => request("post", "/api/v1/partner/me/submit"),
  withdraw: () => request("post", "/api/v1/partner/me/withdraw"),
  directUpload: (body) => request("post", "/api/v1/partner/uploads/direct", body),
  listAssets: (kind) =>
    request("get", "/api/v1/partner/me/assets", undefined, {
      params: kind ? { kind } : undefined,
    }),
  deleteAsset: (id) => request("delete", `/api/v1/partner/me/assets/${id}`),
  adminList: (status) =>
    request("get", "/api/v1/admin/partner-applications", undefined, {
      params: status ? { status } : undefined,
    }),
  adminGet: (id) => request("get", `/api/v1/admin/partner-applications/${id}`),
  adminClaim: (id) => request("post", `/api/v1/admin/partner-applications/${id}/claim`),
  adminApprove: (id) => request("post", `/api/v1/admin/partner-applications/${id}/approve`),
  adminReject: (id, reason) =>
    request("post", `/api/v1/admin/partner-applications/${id}/reject`, { reason }),
  adminSuspend: (id, reason) =>
    request("post", `/api/v1/admin/partner-applications/${id}/suspend`, { reason }),
  adminReinstate: (id) =>
    request("post", `/api/v1/admin/partner-applications/${id}/reinstate`),
  adminScrapeStatus: (orgId, sourceId, body) =>
    request(
      "post",
      `/api/v1/admin/partner-applications/${orgId}/sources/${sourceId}/scrape-status`,
      body
    ),
};

const EXT_MIME = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  bmp: "image/bmp",
  avif: "image/avif",
  heic: "image/heic",
  heif: "image/heif",
  tif: "image/tiff",
  tiff: "image/tiff",
};

function inferMime(file) {
  const raw = String(file.type || "")
    .toLowerCase()
    .split(";")[0]
    .trim();
  if (raw === "image/jpg" || raw === "image/pjpeg") return "image/jpeg";
  if (raw && raw !== "application/octet-stream") return raw;
  const ext = String(file.name || "")
    .split(".")
    .pop()
    ?.toLowerCase();
  return (ext && EXT_MIME[ext]) || raw || "application/octet-stream";
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function reencodeImageToJpeg(file, maxEdge = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        const scale = Math.min(1, maxEdge / Math.max(width, height));
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) {
              reject(new Error("Could not process image. Try a JPG or PNG file."));
              return;
            }
            resolve(
              new File([blob], (file.name || "photo").replace(/\.[^.]+$/, ".jpg"), {
                type: "image/jpeg",
              })
            );
          },
          "image/jpeg",
          quality
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read this image. Try JPG or PNG."));
    };
    img.src = url;
  });
}

/** Upload partner logo / gallery. Key prefix partners/{userId}/ on the server. */
export async function uploadPartnerFile(file, kind) {
  if (!file) throw new Error("No file selected");
  if (!["logo", "gallery"].includes(kind)) {
    throw new Error("Upload kind must be logo or gallery");
  }

  let uploadFile = file;
  const mime = inferMime(file);
  const isImage =
    mime.startsWith("image/") ||
    /\.(jpe?g|png|webp|gif|bmp|avif|heic|heif|tiff?)$/i.test(file.name || "");

  if (!isImage) {
    throw new Error("Please choose an image file (JPG, PNG, WebP, GIF, HEIC, and similar).");
  }
  try {
    uploadFile = await reencodeImageToJpeg(file);
  } catch (err) {
    if (["image/jpeg", "image/png", "image/webp"].includes(mime)) {
      uploadFile = file;
    } else {
      throw err || new Error("Could not process this image. Try JPG, PNG, or WebP.");
    }
  }

  const contentType = inferMime(uploadFile);
  const data = await fileToBase64(uploadFile);

  const result = await partnerApi.directUpload({
    kind,
    contentType,
    size: uploadFile.size,
    filename: uploadFile.name || file.name,
    data,
  });

  if (!result?.asset) {
    throw new Error("Upload failed. Please try again.");
  }
  return result.asset;
}
